<template>
  <v-card v-if="featureEnabled" :class="{ 'mb-6 pa-6': inline }" variant="flat" data-testid="contact-preference-panel">
    <div v-if="!elevated" class="text-title-medium mb-2">{{ useHTranslate('citizen_contact_preference_title') }}</div>
    <p v-dompurify-html="statusText" class="text-body-2 mb-4"></p>

    <div class="d-flex ga-3 flex-wrap">
      <h-action-button
        v-if="status !== 'opted_in'"
        type="primary"
        :loading="saving && requestedStatus === 'opted_in'"
        :disabled="saving"
        @click="choose('opted_in')"
      >
        {{ useHTranslate('citizen_contact_preference_opt_in') }}
      </h-action-button>
      <h-action-button
        v-if="status !== 'opted_out'"
        type="neutral"
        :loading="saving && requestedStatus === 'opted_out'"
        :disabled="saving"
        @click="choose('opted_out')"
      >
        {{ useHTranslate('citizen_contact_preference_opt_out') }}
      </h-action-button>
    </div>
  </v-card>

  <h-action-dialog
    v-model="confirmDialogVisible"
    :title="useHTranslate('citizen_contact_preference_confirm_opt_out_title')"
    :save-text="useHTranslate('citizen_contact_preference_opt_out')"
    :close-text="useHTranslate('citizen_cancel')"
    :save-loading="saving"
    dialog-width="600"
    :class="{ 'profile-child-dialog--elevated': elevated }"
    @save="save('opted_out')"
    @close="confirmDialogVisible = false"
  >
    <p>{{ useHTranslate('citizen_contact_preference_confirm_opt_out_body') }}</p>
  </h-action-dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useHAlerts, useHTranslate } from '@onify/helix-core';
import { useContactDetailsPreference } from '@/onify/citizen/composables/useContactDetailsPreference.js';

const props = defineProps({
  inline: {
    type: Boolean,
    default: false,
  },
  elevated: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['changed']);
const { hAddAlert } = useHAlerts();
const { featureEnabled, status, confirmOptOut, saving, setPreference } = useContactDetailsPreference();
const confirmDialogVisible = ref(false);
const requestedStatus = ref(null);

const statusText = computed(() => {
  if (status.value === 'opted_in') return useHTranslate('citizen_contact_preference_opted_in_body');
  if (status.value === 'opted_out') return useHTranslate('citizen_contact_preference_opted_out_body');
  return useHTranslate('citizen_contact_preference_unknown_body');
});

const save = async (nextStatus) => {
  requestedStatus.value = nextStatus;
  try {
    const changed = await setPreference(nextStatus);
    if (!changed) return;
    confirmDialogVisible.value = false;
    hAddAlert({ type: 'success', title: useHTranslate('citizen_contact_preference_saved'), timeout: 3000 });
    emit('changed', nextStatus);
  } catch (error) {
    console.error('Save contact details preference error:', error);
    hAddAlert({ type: 'error', title: useHTranslate('citizen_contact_preference_save_error'), timeout: 5000 });
  } finally {
    requestedStatus.value = null;
  }
};

const choose = (nextStatus) => {
  if (nextStatus === 'opted_out' && confirmOptOut.value) {
    requestedStatus.value = nextStatus;
    confirmDialogVisible.value = true;
    return;
  }
  save(nextStatus);
};
</script>
